import { useEffect, useImperativeHandle } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import {
	CircularProgress,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	Typography,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { DateTimePicker } from '@mui/x-date-pickers';
import { JSONSchemaType } from 'ajv';
import dayjs, { Dayjs } from 'dayjs';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { NotificationCard } from '~components';
import { CommandTypeEnum, getCommandTypeDisplayName, StatusVariant, UserRoleEnum } from '~enums';
import DevicesService from '~features/devices/services/devicesService';
import { useFormContainer } from '~hooks';
import useAuthorize from '~hooks/useAuthorize';
import { getCommandsForDeviceModel } from '~interfaces/deviceModel';
import { getService } from '~services/serviceFactory';

import schema from './remoteActionSchema.json';
import Dimens from '../../../../themes/dimens';

const deviceService = getService(DevicesService);

type NullablePartial<T> = { [P in keyof T]: T[P] | null };

export type DeviceCommand =
	| {
			command: CommandTypeEnum.EnableOfficeMode;
			endDate: Dayjs;
	  }
	| {
			command: Exclude<CommandTypeEnum, CommandTypeEnum.EnableOfficeMode>;
			endDate?: never;
	  };

interface RemoteActionsDialogProps {
	deviceId: string;
	organisationId: string | undefined;
	onSubmit: (value: DeviceCommand) => void;
	onCancel: () => void;
}

interface RemoteActionFormData {
	command: string;
	schedule: string;
}

/**
 * TODO: Refactor out business logic and data loading.
 * This component should just get a list of RemoteActions and not device and organisation ids.
 */
const RemoteActionsForm = ({
	deviceId,
	organisationId,
	onSubmit,
	onCancel,
}: RemoteActionsDialogProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { setDisabled, formRef } = useFormContainer();
	const { authorize } = useAuthorize();

	const isSuperAdmin = authorize([UserRoleEnum.SuperAdmin]);

	// Load device
	const {
		data: deviceData,
		error: _deviceError,
		isLoading: deviceIsLoading,
	} = useSWR(deviceService.getDeviceByIdCacheKey(deviceId, organisationId), ([_, id, orgId]) =>
		id ? deviceService.getDeviceById(id, orgId ?? undefined) : null
	);

	const { getValues, control, formState } = useForm<NullablePartial<DeviceCommand>>({
		defaultValues: {
			command: null,
			endDate: dayjs().add(15, 'minutes'),
		},
		mode: 'onChange',
		resolver: ajvResolver(schema as JSONSchemaType<RemoteActionFormData>),
	});

	const watchCommand = useWatch({ control, name: 'command' });
	const watchEndDate = useWatch({ control, name: 'endDate' });

	useImperativeHandle(
		formRef,
		() => ({
			onSubmit: () => onSubmit?.(getValues() as DeviceCommand),
			onCancel: () => onCancel?.(),
		}),
		[onSubmit, onCancel, getValues]
	);

	useEffect(() => {
		let disabled = !formState.isValid;
		if (watchCommand === CommandTypeEnum.EnableOfficeMode) {
			if (dayjs(watchEndDate).isBefore(new Date())) {
				disabled = true;
			}
			if (!dayjs(watchEndDate).isValid()) {
				disabled = true;
			}
		}
		setDisabled?.(disabled);
	}, [formState.isValid, watchCommand, watchEndDate, setDisabled]);

	const commandOptions =
		deviceData?.model ? getCommandsForDeviceModel(deviceData?.model, isSuperAdmin) : [];

	if (deviceIsLoading) {
		return (
			<Box
				sx={{
					width: '100%',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress sx={{ color: theme.palette.bgBrandSolid.main }} />
			</Box>
		);
	}
	return (
		<Stack spacing={Dimens.spacingMd}>
			{[1, 2].includes(deviceData?.state?.connectivityProfile?.id ?? 0) && (
				<NotificationCard
					title={t('lowConnectivityProfileTitle', { ns: 'status' })}
					caption={t('lowConnectivityProfileCaption', { ns: 'status' })}
					statusVariant={StatusVariant.Warning}
				/>
			)}
			<Controller
				name='command'
				control={control}
				render={({ field }) => (
					<RadioGroup {...field} sx={{ width: 'fit-content' }}>
						{commandOptions.map((commandOption) => (
							<FormControlLabel
								key={commandOption}
								value={commandOption}
								control={<Radio />}
								sx={{
									translateX: '-40px',
								}}
								label={
									<Typography variant='label'>
										{getCommandTypeDisplayName(commandOption, t)}
									</Typography>
								}
							/>
						))}
					</RadioGroup>
				)}
			/>
			{watchCommand === CommandTypeEnum.EnableOfficeMode && (
				<Controller
					name='endDate'
					control={control}
					render={({ field }) => (
						<DateTimePicker
							label={t('end', { ns: 'date' })}
							value={field.value}
							minTime={dayjs()}
							minDate={dayjs()}
							maxTime={dayjs('2025, 1, 1, 23, 59')}
							maxDate={dayjs().add(1, 'year')}
							onChange={field.onChange}
						/>
					)}
				/>
			)}
		</Stack>
	);
};

export default RemoteActionsForm;
